// The marks on the circle (SPEC-redesign §5.6, §4.4, D-f). Every fact is in two
// places at once — a row on the axis and a mark here — and the two highlight
// each other because both read `viewState.selected` (§4.7).
//
// Marks are DOM, not a GL layer, for the ones that can be opened: they carry
// the accessible names the prototype uses and they are pointer targets. The
// other-vehicle dots open nothing, so D-f makes them decoration and §5.6's
// spike is free to move them wherever the measurement says.
import { TAG } from '../derive/voices.ts';
import type { Item, Mark } from '../shared/model.ts';
import type { ViewState } from '../state/viewstate.ts';

/** What a mark needs to know about the view, without reaching for the store. */
export type MarkContext = {
  /** The resident's lines, in the order they were added. */
  lines: string[];
  /** `sel` — the open item, or null. */
  selected: string | null;
  /** `tylko` — the line lens. Anything outside it is stepped back, not hidden. */
  onlyLine: string | null;
  /** Under the dial's ceiling there are no badges and no tags, only dots (D-9). */
  dial: boolean;
};

export const markContext = (view: ViewState, dial: boolean): MarkContext => ({
  lines: view.lines,
  selected: view.selected,
  onlyLine: view.onlyLine,
  dial,
});

const isMine = (item: Item, lines: string[]) => item.lines?.some((line) => lines.includes(line)) ?? false;

/** The lens matches an item when **any** of its lines is the one named (§4.5). */
const inLens = (item: Item, onlyLine: string | null) =>
  onlyLine == null || (item.lines?.includes(onlyLine) ?? false);

/**
 * What each item is drawn as. Pure, so the choice is read and tested without a
 * map: a vehicle of one of my lines is a numbered badge, any other vehicle is a
 * grey dot, and everything else wears its voice's tag.
 */
export function markFor(item: Item, ctx: MarkContext): Mark | null {
  if (!item.position) return null;
  const selected = ctx.selected === item.id;
  const dimmed = !inLens(item, ctx.onlyLine);

  if (item.kind === 'vehicle' && !isMine(item, ctx.lines)) {
    // Another line's vehicle is context, never a target. Under a lens there is
    // no context to give — the prototype drops every dot while any lens is
    // active, measured 8 → 0 — and in the dial the dots are all there is.
    return ctx.onlyLine == null ? { kind: 'dot', id: item.id, position: item.position } : null;
  }
  // The dial is below the legibility floor the rest of the design keeps, so it
  // shows no badge and no tag at all — dots only, plus the text line (D-9).
  if (ctx.dial) return { kind: 'dot', id: item.id, position: item.position };
  if (item.kind === 'vehicle') return { kind: 'badge', id: item.id, item, selected, dimmed };
  return { kind: 'tag', id: item.id, item, selected, dimmed };
}

/** The marks for one axis, in axis order — the circle's DOM order follows it. */
export function marksFor(marked: Item[], ctx: MarkContext): Mark[] {
  const marks: Mark[] = [];
  for (const item of marked) {
    const mark = markFor(item, ctx);
    if (mark) marks.push(mark);
  }
  return marks;
}

/**
 * „Tramwaj 16, Os. Sobieskiego" — the accessible name the prototype gives a
 * badge, read out of the running prototype rather than invented here. A
 * vehicle's `title` is „16 → Os. Sobieskiego" (§4.4), so the two halves are
 * split back apart; everything else is named by its title, as the prototype
 * names „Koncert w Blue Note" and „Poznań, ul. Dąbrowskiego 92".
 */
export function markName(item: Item): string {
  if (item.kind !== 'vehicle') return item.title;
  const kind = item.mode === 'tram' ? 'Tramwaj' : 'Autobus';
  const [line, headsign] = item.title.split(' → ');
  return headsign ? `${kind} ${line}, ${headsign}` : `${kind} ${line ?? item.title}`;
}

/** The number on a badge: the line, never the vehicle's own id (§4.4). */
export const badgeLabel = (item: Item): string => item.lines?.[0] ?? item.title;

/**
 * The name a voice wears on its mark (§5.6) — the axis table itself, not a copy
 * of it. A mark and its row are the same fact in two places (§4.7), so they say
 * the same word: a geocoded notice reads „ZTM·K" here exactly as it does on the
 * rail, in the footer and in „Warstwy", and the two „jadę" voices stay apart.
 */
export const tagLabel = (item: Item): string => TAG[item.voice];

/**
 * What a merged tag counts, in Polish, per voice: „3 wydarzenia w tym miejscu"
 * for the calendar but „2 awarie" for Aquanet and „2 komunikaty" for ZTM. The
 * plural machinery is voice-agnostic; only the noun is not, and one noun for
 * every voice had a cluster of water faults announcing itself as events.
 */
type Counted = [one: string, few: string, many: string];
const EVENTS: Counted = ['wydarzenie', 'wydarzenia', 'wydarzeń'];
const COUNTED: Record<string, Counted> = {
  pl: EVENTS,
  aq: ['awaria', 'awarie', 'awarii'],
  ztmk: ['komunikat', 'komunikaty', 'komunikatów'],
  ztm: ['pojazd', 'pojazdy', 'pojazdów'],
};
export const countedNoun = (item: Item): Counted => COUNTED[item.voice] ?? EVENTS;
